import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Pin, Bookmark, Clock, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useListingSuggestions } from "@/hooks/useListingSuggestions";
import { getThumbnailUrl } from "@/utils/imageOptimization";


interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  placeholder?: string;
  className?: string;
  user?: any;
  onPinCurrentSearch?: () => void;
  showPinButton?: boolean;
  onFocus?: () => void;
  showPinnedInDropdown?: boolean;
  allowDirectPin?: boolean;
  onPinSuccess?: () => void;
}

interface RecentSearch {
  id: string;
  search_term: string;
}

interface SavedSearch {
  id: string;
  search_term: string;
}

export const SearchInput = ({ 
  value, 
  onChange, 
  onSubmit,
  placeholder = "Sök efter bildelar...",
  className,
  user,
  onPinCurrentSearch,
  showPinButton = false,
  onFocus,
  showPinnedInDropdown = false,
  allowDirectPin = false,
  onPinSuccess
}: SearchInputProps) => {
  const { toast } = useToast();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const navigate = useNavigate();
  const { suggestions: listingSuggestions } = useListingSuggestions(value, 5);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);

  const [filteredSuggestions, setFilteredSuggestions] = useState<RecentSearch[]>([]);
  const [pinnedSearchIds, setPinnedSearchIds] = useState<Set<string>>(new Set());
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Fetch recent searches when user is logged in
  useEffect(() => {
    if (user) {
      fetchRecentSearches();
      fetchPinnedSearches();
      if (showPinnedInDropdown) {
        fetchSavedSearches();
        
        // Set up real-time subscription for saved searches
        const channel = supabase
          .channel(`saved-searches-dropdown:${user.id}`, { config: { private: true } })
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'saved_searches',
              filter: `user_id=eq.${user.id}`
            },
            () => {
              fetchSavedSearches();
              fetchPinnedSearches();
            }
          )
          .subscribe();

        return () => {
          supabase.removeChannel(channel);
        };
      }
    } else {
      setRecentSearches([]);
      setPinnedSearchIds(new Set());
      setSavedSearches([]);
    }
  }, [user, showPinnedInDropdown]);

  const fetchSavedSearches = async () => {
    if (!user) return;

    const { data } = await supabase
      .from('saved_searches')
      .select('id, search_term')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(5);

    if (data) {
      setSavedSearches(data);
      // Also update the pinned IDs set
      setPinnedSearchIds(new Set(data.map(s => s.search_term)));
    }
  };

  const fetchPinnedSearches = async () => {
    if (!user) return;

    const { data } = await supabase
      .from('saved_searches')
      .select('search_term')
      .eq('user_id', user.id);

    if (data) {
      setPinnedSearchIds(new Set(data.map(s => s.search_term)));
    }
  };

  // Filter suggestions based on input
  useEffect(() => {
    if (value.trim() && recentSearches.length > 0) {
      const filtered = recentSearches.filter(search =>
        search.search_term.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions(recentSearches);
    }
  }, [value, recentSearches]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchRecentSearches = async () => {
    if (!user) return;

    const { data } = await supabase
      .from('recent_searches')
      .select('id, search_term')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(5);

    if (data) {
      setRecentSearches(data);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (searchTerm: string) => {
    onChange(searchTerm);
    setShowSuggestions(false);
    onSubmit(searchTerm);
  };

  const handleListingClick = (listingId: string) => {
    setShowSuggestions(false);
    navigate(`/listing/${listingId}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setShowSuggestions(false);
      // Don't call onSubmit here - let the form handle it
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleFocus = () => {
    setShowSuggestions(true);
    if (onFocus) {
      onFocus();
    }
  };


  const unpinSearch = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Find the search term before deletion
    const searchToUnpin = savedSearches.find(s => s.id === id);
    
    const { error } = await supabase
      .from('saved_searches')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: "Kunde inte ta bort sökning",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setSavedSearches(savedSearches.filter(s => s.id !== id));
      // Remove from pinnedSearchIds so it can be pinned again
      if (searchToUnpin) {
        setPinnedSearchIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(searchToUnpin.search_term);
          return newSet;
        });
      }
      toast({
        title: "Sökning borttagen",
        description: "Sökningen har tagits bort från dina pinnade sökningar",
      });
    }
  };

  const pinSearch = async (searchTerm: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;

    // Check if already at limit
    const { count } = await supabase
      .from('saved_searches')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id);

    if (count && count >= 5) {
      toast({
        title: "Maximum nått",
        description: "Du kan bara ha 5 pinnade sökningar åt gången",
        variant: "destructive",
      });
      return;
    }

    // Allow duplicates - just insert
    const { error } = await supabase
      .from('saved_searches')
      .insert({
        user_id: user.id,
        search_term: searchTerm,
      });

    if (error) {
      toast({
        title: "Kunde inte pinna sökning",
        description: error.message,
        variant: "destructive",
      });
    } else {
      // Refresh saved searches to show the new pin immediately
      if (showPinnedInDropdown) {
        await fetchSavedSearches();
      }
      // Notify parent component to refresh
      if (onPinSuccess) {
        onPinSuccess();
      }
      toast({
        title: "Sökning pinnad!",
        description: "Sökningen har lagts till i dina pinnade sökningar",
      });
    }
  };

  const handleDirectPin = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (value.trim()) {
      await pinSearch(value.trim(), e);
      // Keep dropdown open to show the new pin
      setShowSuggestions(true);
    }
  };

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
      <Input
        type="text"
        placeholder={placeholder}
        className={cn("pl-10", user && value.trim() && ((showPinButton && onPinCurrentSearch) || allowDirectPin) ? "pr-10" : "", className)}
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      />
      {user && value.trim() && ((showPinButton && onPinCurrentSearch) || allowDirectPin) && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={(e) => {
            if (allowDirectPin) {
              handleDirectPin(e);
            } else if (onPinCurrentSearch) {
              onPinCurrentSearch();
            }
          }}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 z-10"
          title="Pinna sökning"
          aria-label="Pinna sökning"
        >
          <Bookmark className="h-4 w-4" />
        </Button>
      )}
      
      {showSuggestions && (listingSuggestions.length > 0 || savedSearches.length > 0 || filteredSuggestions.length > 0) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg z-50 max-h-96 overflow-auto">
          {/* Live listing results */}
          {listingSuggestions.length > 0 && (
            <div className="py-1 border-b border-border">
              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground">
                Annonser
              </div>
              {listingSuggestions.map((listing) => (
                <button
                  key={listing.id}
                  type="button"
                  onClick={() => handleListingClick(listing.id)}
                  className="w-full px-3 py-2 hover:bg-accent flex items-center gap-3 text-left"
                >
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-md overflow-hidden bg-muted flex-shrink-0">
                    {listing.images?.[0] ? (
                      <img
                        src={getThumbnailUrl(listing.images[0])}
                        alt={listing.title}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center">
                        <Search className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{listing.title}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {listing.car_brand} {listing.car_model}
                    </div>
                  </div>
                  <div className="text-sm font-semibold whitespace-nowrap">
                    {Math.round(Number(listing.price)).toLocaleString('sv-SE')} kr
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Pinned Searches - Horizontal Pills at Top */}
          {showPinnedInDropdown && savedSearches.length > 0 && (

            <div className="p-4 border-b border-border">
              <div className="flex flex-wrap gap-2">
                {savedSearches.map((search) => (
                  <button
                    key={search.id}
                    onClick={() => handleSuggestionClick(search.search_term)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground hover:bg-primary/90 text-sm rounded-full transition-all shadow-sm hover:shadow group"
                  >
                    <span className="font-medium">{search.search_term}</span>
                    <button
                      onClick={(e) => unpinSearch(search.id, e)}
                      className="ml-0.5 hover:bg-primary-foreground/20 rounded-full p-1 transition-colors"
                      title="Ta bort"
                      aria-label="Ta bort pinnad sökning"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Recent Searches - Compact List */}
          {filteredSuggestions.length > 0 && (
            <div className="py-1">
              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground">
                Senaste sökningar
              </div>
              {filteredSuggestions.map((search) => (
                <div
                  key={search.id}
                  className="px-3 py-2.5 hover:bg-accent cursor-pointer flex items-center gap-3 justify-between group"
                  onClick={() => handleSuggestionClick(search.search_term)}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm truncate">{search.search_term}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => pinSearch(search.search_term, e)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0 flex-shrink-0"
                    title="Pinna sökning"
                    aria-label="Pinna sökning"
                  >
                    <Pin className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
